/**
 * Development-only state harness.
 *
 * Loading, empty, error and low-data states are hard to reach on demand: they
 * need a broken network, a brand-new Google property, or a site with no
 * traffic. Screenshotting them for a design review used to mean editing a
 * component, taking the picture and reverting the edit, which is exactly the
 * kind of undocumented manual step a new owner would have to rediscover.
 *
 * Instead, append `?fixture=<name>` to the dashboard URL in development.
 *
 *   yarn start
 *   open http://localhost:3000/dashboard?fixture=api-error
 *
 * The full list, with what each one proves, is in `docs/design-qa.md`.
 *
 * Safety: every export is guarded by `process.env.NODE_ENV !== "production"`.
 * CRA replaces that expression with a literal at build time, so the whole
 * branch is dead code in a production bundle and the minifier removes it.
 * `yarn verify:bundle` asserts that no fixture identifier survives the build.
 */

/** @typedef {"loading"|"low-data"|"api-error"|"empty-insights"|"chat-error"|"indexing-unavailable"} FixtureName */

/** Recognised fixtures. The key is what you pass to `?fixture=`. */
export const FIXTURES = {
  loading: "Every block held in its skeleton state.",
  "low-data": "A property Google has only shared a few days of data for.",
  "api-error": "The dashboard request fails. Error state plus retry.",
  "empty-insights": "The request succeeds but returns no opportunities.",
  "chat-error": "The chat stream fails mid-answer.",
  "indexing-unavailable": "Search Console withholds index coverage.",
};

const isDev = process.env.NODE_ENV !== "production";

/**
 * The fixture requested by the current URL, or `null`.
 * Always `null` in a production build.
 *
 * @returns {FixtureName|null}
 */
export function activeFixture() {
  if (!isDev || typeof window === "undefined") return null;
  const name = new URLSearchParams(window.location.search).get("fixture");
  return name && Object.prototype.hasOwnProperty.call(FIXTURES, name) ? name : null;
}

/**
 * True when the named fixture is active.
 *
 * @param {FixtureName} name
 * @returns {boolean}
 */
export function isFixture(name) {
  return activeFixture() === name;
}
